// A store's size, what it does when full and its copies, in the person's
// words (02-DESIGN/10-state/01-stores-types-and-instances.md § size, when
// full, and copies; decision 0050): the size read from what a person types,
// how full a store is, and the words for the two policies and the copies.

import { MAX_SNAPSHOT, NEAR_FULL, WhenFull, type Store } from "../../wire/state.ts";

const UNITS: readonly (readonly [string, number])[] = [
  ["kib", 2 ** 10],
  ["mib", 2 ** 20],
  ["gib", 2 ** 30],
  ["tib", 2 ** 40],
  ["kb", 1e3],
  ["mb", 1e6],
  ["gb", 1e9],
  ["tb", 1e12],
  ["k", 2 ** 10],
  ["m", 2 ** 20],
  ["g", 2 ** 30],
  ["b", 1],
];

/**
 * parseSize reads a size as a person types it — "256 MiB", "1GiB", "512k",
 * or bytes — and answers it in bytes; undefined for what is not a size.
 */
export function parseSize(text: string): number | undefined {
  let t = text.trim().toLowerCase();
  let mult = 1;
  for (const [suffix, m] of UNITS) {
    if (t.endsWith(suffix)) {
      mult = m;
      t = t.slice(0, -suffix.length).trim();
      break;
    }
  }
  if (t === "" || !/^\d+(\.\d+)?$/.test(t)) {
    return undefined;
  }
  const n = Math.floor(Number(t) * mult);
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/** Fullness is how full a store is: below nine tenths, near full past them, full when less than one snapshot's room is left. */
export type Fullness = "room" | "near" | "full";

/** fullness reads a store's fill against its size. */
export function fullness(st: Pick<Store, "fill" | "budget">): Fullness {
  if (st.budget <= 0) {
    return "room";
  }
  if (st.budget - st.fill < MAX_SNAPSHOT) {
    return "full";
  }
  return st.fill >= NEAR_FULL * st.budget ? "near" : "room";
}

/** compacts reports whether the store makes room when full. */
export function compacts(st: Pick<Store, "when_full">): boolean {
  return st.when_full === WhenFull.compact;
}

/** whenFullWords is the store's policy at its size, as the console says it. */
export function whenFullWords(st: Pick<Store, "when_full">): string {
  return compacts(st) ? "Make room by keeping only current state" : "Refuse new changes";
}

/** copiesWords is the store's copies, as the console says them. */
export function copiesWords(st: Pick<Store, "replicas">): string {
  return st.replicas === 3 ? "Three copies" : "One copy";
}
